import { useEffect, useState } from 'react';
import { apiClient, ApiError, type ProjectLink } from '@/lib/api/client';
import { cn } from '@/lib/utils';
import { RelativeTime } from '@/components/RelativeTime';

interface Props {
  projectId: string;
  myUserId: string;
}

/**
 * Project links — flat list of {label, url} maintained by any project member.
 * Author or owner/admin can delete. Polls every 30s (links rarely change).
 */
export function ProjectLinks({ projectId, myUserId }: Props) {
  const [links, setLinks] = useState<ProjectLink[] | null>(null);
  const [myRole, setMyRole] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [label, setLabel] = useState('');
  const [url, setUrl] = useState('');
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);

  const canModerate = myRole === 'owner' || myRole === 'admin';

  async function refresh(silent = false) {
    try {
      const r = await apiClient.listProjectLinks(projectId);
      setLinks(r.links);
      setMyRole(r.my_role);
      if (!silent) setErr(null);
    } catch (e) {
      if (!silent) setErr(e instanceof ApiError ? e.message : 'Failed to load links');
    }
  }

  useEffect(() => {
    refresh();
    const t = setInterval(() => {
      if (document.visibilityState === 'visible') refresh(true);
    }, 30000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  async function add() {
    const lbl = label.trim();
    const u = normalizeUrl(url.trim());
    if (!lbl || !u) {
      setErr('Both label and URL are required.');
      return;
    }
    setAdding(true);
    setErr(null);
    const tempId = `temp-${Date.now()}`;
    const optimistic: ProjectLink = {
      link_id: tempId,
      user_id: myUserId,
      user_email: 'you',
      user_name: null,
      label: lbl,
      url: u,
      created_at: Date.now(),
    };
    setLinks((l) => (l ? [...l, optimistic] : [optimistic]));
    try {
      const r = await apiClient.createProjectLink(projectId, lbl, u);
      setLinks((l) => (l ? l.map((x) => (x.link_id === tempId ? r.link : x)) : l));
      setLabel('');
      setUrl('');
      setShowForm(false);
    } catch (e) {
      setLinks((l) => (l ? l.filter((x) => x.link_id !== tempId) : l));
      setErr(e instanceof ApiError ? e.message : 'Add failed');
    } finally {
      setAdding(false);
    }
  }

  async function destroy(id: string) {
    if (pendingDelete !== id) {
      setPendingDelete(id);
      setTimeout(() => {
        setPendingDelete((curr) => (curr === id ? null : curr));
      }, 3000);
      return;
    }
    setPendingDelete(null);
    try {
      await apiClient.deleteProjectLink(id);
      setLinks((l) => (l ? l.filter((x) => x.link_id !== id) : l));
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Delete failed');
    }
  }

  return (
    <section
      aria-labelledby="project-links-heading"
      className="section-glow rounded-lg bg-card/30"
    >
      <header className="flex items-center justify-between border-b border-border/60 px-4 py-2">
        <h2
          id="project-links-heading"
          className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground"
        >
          Links
        </h2>
        <div className="flex items-center gap-3">
          <span className="font-mono text-[10px] tabular-nums text-muted-foreground/60">
            {links?.length ?? 0}
          </span>
          {!showForm && (
            <button
              type="button"
              onClick={() => setShowForm(true)}
              className="rounded px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-widest text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
            >
              + add
            </button>
          )}
        </div>
      </header>

      {showForm && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            add();
          }}
          className="grid gap-2 border-b border-border/60 px-3 py-2 sm:grid-cols-[1fr_2fr_auto]"
        >
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Label (e.g. Spec doc)"
            maxLength={140}
            autoFocus
            className="rounded-md border border-border bg-card px-2 py-1.5 text-sm text-foreground placeholder:text-muted-foreground/60 focus:border-ring focus:outline-none"
          />
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://…"
            maxLength={2000}
            className="rounded-md border border-border bg-card px-2 py-1.5 text-sm text-foreground placeholder:text-muted-foreground/60 focus:border-ring focus:outline-none"
          />
          <div className="flex items-center gap-1">
            <button
              type="submit"
              disabled={adding || !label.trim() || !url.trim()}
              className="rounded-md bg-primary px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest text-primary-foreground transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {adding ? 'adding…' : 'add'}
            </button>
            <button
              type="button"
              onClick={() => {
                setShowForm(false);
                setLabel('');
                setUrl('');
                setErr(null);
              }}
              className="rounded-md px-2 py-1.5 font-mono text-[10px] uppercase tracking-widest text-muted-foreground transition-colors hover:text-foreground"
            >
              cancel
            </button>
          </div>
        </form>
      )}

      <ul className="divide-y divide-border/40">
        {links === null ? (
          <li className="px-4 py-3" aria-busy="true">
            <div className="h-5 animate-pulse rounded bg-background/40" />
          </li>
        ) : links.length === 0 && !showForm ? (
          <li className="px-4 py-6 text-center text-xs text-muted-foreground/70">
            No links yet.
          </li>
        ) : (
          links.map((l) => {
            const isMine = l.user_id === myUserId;
            const isTemp = l.link_id.startsWith('temp-');
            const canDelete = !isTemp && (isMine || canModerate);
            return (
              <li key={l.link_id} className="group/lnk flex items-center gap-3 px-4 py-2">
                <a
                  href={l.url}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="min-w-0 flex-1 truncate text-sm text-foreground hover:text-primary"
                  title={l.url}
                >
                  <span className="font-medium">{l.label}</span>
                  <span className="ml-2 truncate font-mono text-[11px] text-muted-foreground/70">
                    {prettyHost(l.url)}
                  </span>
                </a>
                <RelativeTime ts={l.created_at} className="shrink-0 cursor-pointer rounded font-mono text-[10px] tabular-nums text-muted-foreground/50 transition-colors hover:text-foreground" />
                {canDelete && (
                  <button
                    type="button"
                    onClick={() => destroy(l.link_id)}
                    className={cn(
                      'shrink-0 rounded px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-widest transition-colors opacity-0 group-hover/lnk:opacity-100 focus-visible:opacity-100 [@media(hover:none)]:opacity-100',
                      pendingDelete === l.link_id
                        ? 'border border-destructive/70 bg-destructive/15 text-destructive opacity-100'
                        : 'text-muted-foreground/60 hover:text-destructive',
                    )}
                    title={
                      pendingDelete === l.link_id
                        ? 'Click again to confirm'
                        : !isMine
                          ? 'Delete (admin)'
                          : 'Delete'
                    }
                  >
                    {pendingDelete === l.link_id ? 'confirm' : 'del'}
                  </button>
                )}
              </li>
            );
          })
        )}
      </ul>

      {err && (
        <div
          role="alert"
          className="border-t border-destructive/40 bg-destructive/10 px-4 py-2 text-xs text-foreground"
        >
          {err}
        </div>
      )}
    </section>
  );
}

/** If the user typed a bare host (no scheme), add https:// so it round-trips. */
function normalizeUrl(s: string): string | null {
  if (!s) return null;
  const withScheme = /^https?:\/\//i.test(s) ? s : `https://${s}`;
  try {
    const u = new URL(withScheme);
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return null;
    return u.toString();
  } catch {
    return null;
  }
}

function prettyHost(u: string): string {
  try {
    const { host, pathname } = new URL(u);
    return host + (pathname && pathname !== '/' ? pathname : '');
  } catch {
    return u;
  }
}
